/**
 * PWA Cache Management Utilities
 * Note: Offline caching is disabled. This utility only manages localStorage, sessionStorage, and browser storage.
 */

export interface ClearCacheResult {
  success: boolean;
  message: string;
  details?: string[];
}

/**
 * Clear browser storage and caches (NO OFFLINE CACHE - app works online only)
 * Note: Service worker is kept for PWA installability but doesn't cache anything
 */
export async function clearPWACache(): Promise<ClearCacheResult> {
  const details: string[] = [];
  
  try {
    // 1. Clear all browser caches (should be empty since we don't cache offline anymore)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      if (cacheNames.length > 0) {
        await Promise.all(
          cacheNames.map(async (cacheName) => {
            await caches.delete(cacheName);
            details.push(`Deleted cache: ${cacheName}`);
          })
        );
      } else {
        details.push('No caches found (expected - offline caching disabled)');
      }
    }

    // 2. Clear localStorage
    if (typeof Storage !== 'undefined') {
      const localStorageKeys = Object.keys(localStorage);
      localStorage.clear();
      details.push(`Cleared localStorage (${localStorageKeys.length} items)`);
    }

    // 3. Clear sessionStorage
    if (typeof Storage !== 'undefined') {
      const sessionStorageKeys = Object.keys(sessionStorage);
      sessionStorage.clear();
      details.push(`Cleared sessionStorage (${sessionStorageKeys.length} items)`);
    }

    // 4. DO NOT unregister service worker (needed for PWA installability)
    // Just note that it exists
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        details.push(`Service worker active (kept for PWA installability)`);
      }
    }

    // 5. Clear IndexedDB (if any)
    if ('indexedDB' in window) {
      try {
        const databases = await indexedDB.databases();
        await Promise.all(
          databases.map(async (db) => {
            if (db.name) {
              const deleteReq = indexedDB.deleteDatabase(db.name);
              await new Promise((resolve, reject) => {
                deleteReq.onsuccess = () => resolve(void 0);
                deleteReq.onerror = () => reject(deleteReq.error);
              });
              details.push(`Deleted IndexedDB: ${db.name}`);
            }
          })
        );
      } catch (error) {
        details.push(`IndexedDB cleanup warning: ${error}`);
      }
    }

    return {
      success: true,
      message: 'Browser storage cleared successfully. Please refresh the page.',
      details,
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to clear storage: ${error}`,
      details,
    };
  }
}

/**
 * Clear only authentication-related cache and storage
 */
/**
 * Clear version tracking from localStorage
 * Forces update detection on next check
 */
export function clearVersionCache(): ClearCacheResult {
  try {
    if (typeof window === 'undefined') {
      return { success: false, message: 'Not in browser context' };
    }

    localStorage.removeItem('app_version');

    return {
      success: true,
      message: 'Version cache cleared successfully',
      details: ['localStorage: app_version removed'],
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to clear version cache: ${error}`,
    };
  }
}

export function clearAuthCache(): ClearCacheResult {
  const details: string[] = [];
  
  try {
    // Clear localStorage items related to auth
    const authKeys = Object.keys(localStorage).filter(key => 
      key.includes('next-auth') || 
      key.includes('auth') || 
      key.includes('token') ||
      key.includes('session')
    );
    
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      details.push(`Removed localStorage: ${key}`);
    });

    // Clear auth-related cookies
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name.includes('next-auth') || name.includes('__Secure-next-auth')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        details.push(`Cleared cookie: ${name}`);
      }
    });

    return {
      success: true,
      message: 'Authentication cache cleared. Please refresh the page.',
      details,
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to clear auth cache: ${error}`,
      details,
    };
  }
}

/**
 * Get cache information for debugging
 */
export async function getCacheInfo(): Promise<{
  caches: string[];
  localStorage: number;
  sessionStorage: number;
  serviceWorkers: number;
}> {
  const info = {
    caches: [] as string[],
    localStorage: 0,
    sessionStorage: 0,
    serviceWorkers: 0,
  };

  try {
    // Cache names
    if ('caches' in window) {
      info.caches = await caches.keys();
    }

    // Storage counts
    if (typeof Storage !== 'undefined') {
      info.localStorage = Object.keys(localStorage).length;
      info.sessionStorage = Object.keys(sessionStorage).length;
    }

    // Service worker count
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      info.serviceWorkers = registrations.length;
    }
  } catch (error) {
    console.warn('Failed to get cache info:', error);
  }

  return info;
}
